import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMesaHabilitacion, getOrdersMock, MOCK_ZONAS, updateMesaHabilitacion } from './mockOperaciones';

const AccesoMesas = () => {
  const navigate = useNavigate();
  const { zoneId } = useParams();
  const zone = useMemo(() => MOCK_ZONAS.find((z) => String(z.id) === String(zoneId)) || null, [zoneId]);

  const [orders, setOrders] = useState([]);
  const [habilitacion, setHabilitacion] = useState(() => getMesaHabilitacion());

  useEffect(() => {
    setOrders(getOrdersMock());
  }, []);

  const getHabilitacionMesa = useCallback(
    (mesaId) => {
      const z = habilitacion[String(zoneId)];
      const v = z?.[String(mesaId)];
      return v === 'ocupada' ? 'ocupada' : 'disponible';
    },
    [habilitacion, zoneId]
  );

  const toggleMesaHabilitacion = (e, mesaId) => {
    e.preventDefault();
    e.stopPropagation();
    const actual = getHabilitacionMesa(mesaId);
    const siguiente = actual === 'ocupada' ? 'disponible' : 'ocupada';
    setHabilitacion(updateMesaHabilitacion(zoneId, mesaId, siguiente));
  };

  const ordersAbiertos = useMemo(() => orders.filter((o) => o.zoneId === Number(zoneId) && o.estado === 'abierto'), [orders, zoneId]);

  const openOrderIds = useMemo(() => {
    const open = new Set();
    ordersAbiertos.forEach((o) => open.add(o.tipo === 'bar' ? 'bar' : String(o.mesaId)));
    return open;
  }, [ordersAbiertos]);

  if (!zone) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="text-red-600 font-medium">Zona no encontrada</div>
      </div>
    );
  }

  const openMesa = (mesaId) => {
    navigate(`/restaurantes/operaciones/mesa/${zone.id}/${mesaId}`);
  };

  const openBarra = () => {
    navigate(`/restaurantes/operaciones/bar/${zone.id}`);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Acceso - {zone.nombreZona}</h1>
          <p className="text-gray-600 text-sm mt-1">
            Vista gráfica demo: cada <span className="font-medium">mesa</span> puede estar disponible u ocupada (habilitación).
            La <span className="font-medium">barra</span> solo muestra si hay pedido abierto (sin habilitación).
          </p>
        </div>
        <button
          type="button"
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
          onClick={() => navigate('/restaurantes/operaciones/acceso')}
        >
          Cambiar zona
        </button>
      </div>

      {zone.tieneBarra && (
        <div className="mb-6">
          <button
            type="button"
            onClick={openBarra}
            className={`w-full text-left px-5 py-4 rounded-lg border transition-colors ${
              openOrderIds.has('bar') ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
            } hover:border-teal-300`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs text-gray-500">Barra</div>
                <div className="text-lg font-semibold text-teal-800">
                  {openOrderIds.has('bar') ? 'Pedido abierto' : 'Sin pedidos abiertos'}
                </div>
              </div>
              <div className="text-gray-400 text-2xl">🍸</div>
            </div>
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {zone.mesas.map((mesaId) => {
          const ocupada = getHabilitacionMesa(mesaId) === 'ocupada';
          const pedidoAbierto = openOrderIds.has(String(mesaId));
          const borderClass = ocupada
            ? 'border-amber-400 bg-amber-50/50'
            : pedidoAbierto
              ? 'border-teal-500 bg-teal-50'
              : 'border-gray-200 bg-white';

          return (
            <div key={mesaId} className={`rounded-lg border overflow-hidden transition-colors ${borderClass}`}>
              <button
                type="button"
                onClick={() => openMesa(mesaId)}
                className="w-full text-left px-4 py-3 hover:bg-black/[0.02] transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-gray-500">Mesa</div>
                    <div className="text-xl font-bold text-gray-900">{mesaId}</div>
                  </div>
                  <div className="text-gray-400 text-2xl">🍽️</div>
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div>
                    <span className="text-gray-500">Habilitación:</span>{' '}
                    <span className={`font-semibold ${ocupada ? 'text-amber-800' : 'text-green-700'}`}>
                      {ocupada ? 'Ocupada' : 'Disponible'}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    {pedidoAbierto ? (
                      <span className="font-medium text-teal-800">Pedido abierto</span>
                    ) : (
                      <span>Libre</span>
                    )}
                  </div>
                </div>
              </button>
              <div className="px-3 pb-3 pt-0">
                <button
                  type="button"
                  onClick={(e) => toggleMesaHabilitacion(e, mesaId)}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-amber-300 text-amber-900 bg-white/80 hover:bg-amber-50 font-medium"
                >
                  {ocupada ? 'Marcar disponible' : 'Marcar ocupada'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccesoMesas;

